/**
 * ApiCostStatusCard — admin-only condensed cost summary for the System
 * Status page. Shows this-month total, projected, budget tracker, and a
 * deep-link to the full /admin/api-costs dashboard.
 *
 * Backed by GET /api/admin/api-costs/budget which already returns the
 * by-category breakdown, projected total, and status colour.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { apiGet } from "@/lib/api";

const fmtUsd = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const CATEGORY_ORDER = [
  "report_analysis",
  "ai_chat",
  "rationale",
  "market_pdf",
  "company_score",
  "sector_analysis",
];

const STATUS = {
  ok: { dot: "bg-success", text: "text-success", border: "border-success/40", label: "Under budget" },
  warn: { dot: "bg-warning", text: "text-warning", border: "border-warning/40", label: "Approaching budget" },
  over: { dot: "bg-destructive", text: "text-destructive", border: "border-destructive/40", label: "Budget exceeded" },
};

const ApiCostStatusCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet("/admin/api-costs/budget")
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card data-testid="api-cost-status-card-loading">
        <CardContent className="p-5">
          <Skeleton className="h-6 w-40 mb-3" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const tone = STATUS[data.status || "ok"];
  const pctUsed = Math.min(100, Number(data.pct_used) || 0);

  return (
    <Card className={`border ${tone.border}`} data-testid="api-cost-status-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <Coins className="h-4 w-4 text-primary" />
              API Cost Tracking
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-normal">
                Admin only
              </span>
            </CardTitle>
            <CardDescription className="mt-1">
              Total Claude / OpenAI / Gemini spend this month, broken down by category.
            </CardDescription>
          </div>
          <Link to="/admin/api-costs" data-testid="api-cost-status-card-deeplink">
            <Button variant="outline" size="sm">
              View full breakdown <ArrowRight className="h-3 w-3 ml-1.5" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-md border bg-muted/20 p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">This month</div>
            <div className="text-lg font-display font-semibold tabular-nums" data-testid="api-cost-card-this-month">
              {fmtUsd(data.this_month_usd)}
            </div>
            <div className="text-[10px] text-muted-foreground">
              Day {data.days_elapsed}/{data.days_in_month}
            </div>
          </div>
          <div className="rounded-md border bg-muted/20 p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Projected</div>
            <div className="text-lg font-display font-semibold tabular-nums" data-testid="api-cost-card-projected">
              {fmtUsd(data.projected_month_usd)}
            </div>
            <div className="text-[10px] text-muted-foreground">{data.pct_projected}% of budget</div>
          </div>
          <div className="rounded-md border bg-muted/20 p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last month</div>
            <div className="text-lg font-display font-semibold tabular-nums" data-testid="api-cost-card-last-month">
              {fmtUsd(data.last_month_usd)}
            </div>
            <div className="text-[10px] text-muted-foreground">Final total</div>
          </div>
          <div className="rounded-md border bg-muted/20 p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Budget</div>
            <div className="text-lg font-display font-semibold tabular-nums" data-testid="api-cost-card-budget">
              {fmtUsd(data.budget?.monthly_budget_usd)}
            </div>
            <div className="text-[10px] text-muted-foreground">
              Alert at {data.budget?.alert_threshold_pct ?? 80}%
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5 text-xs">
            <div className="flex items-center gap-2">
              {data.status === "ok" ? (
                <CheckCircle2 className={`h-3.5 w-3.5 ${tone.text}`} />
              ) : (
                <AlertTriangle className={`h-3.5 w-3.5 ${tone.text}`} />
              )}
              <span className={`font-medium ${tone.text}`} data-testid="api-cost-card-status">
                {tone.label}
              </span>
              <span className="text-muted-foreground">· {data.pct_used}% used</span>
            </div>
            <span className="text-muted-foreground tabular-nums">
              {fmtUsd(data.this_month_usd)} / {fmtUsd(data.budget?.monthly_budget_usd)}
            </span>
          </div>
          <Progress value={pctUsed} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11.5px]">
          {CATEGORY_ORDER.map((cat) => {
            const row = data.by_category?.[cat];
            if (!row) return null;
            return (
              <div
                key={cat}
                className="flex items-center justify-between rounded-md border bg-background/60 px-2.5 py-1.5"
                data-testid={`api-cost-card-cat-${cat}`}
              >
                <span className="text-muted-foreground truncate pr-2">{row.label}</span>
                <span className="font-mono tabular-nums font-semibold">{fmtUsd(row.cost_usd)}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ApiCostStatusCard;
